import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { linkedInLogin } from '../../redux/LIAuthSlice/liAuthSlice';

// Shown across network pages (Networking, My Network, Inbox, Feed) when the
// user hasn't connected LinkedIn yet. The connect popup itself is opened by
// Navbaar's global `linkedInAuthUrl` watcher, so this only dispatches the login.
const FEATURES = [
    {
        color: '#3F57E0',
        t: 'Warm intros, ranked',
        d: 'See your top connections at target companies, ordered by referral probability.',
        icon: (<svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>),
    },
    {
        color: '#7C3AED',
        t: 'AI-drafted outreach',
        d: 'Champ writes personalized intros referencing your shared connections and roles.',
        icon: (<svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.6 4.6L18 9.2l-4.4 1.6L12 15l-1.6-4.2L6 9.2l4.4-1.6L12 3z" /><path d="M19 14l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2z" /><path d="M5 4l.5 1.5L7 6l-1.5.5L5 8l-.5-1.5L3 6l1.5-.5L5 4z" /></svg>),
    },
    {
        color: '#F59E0B',
        t: 'Hiring signals from peers',
        d: "Know who's actively hiring or recently posted — surfaced as soon as they update.",
        icon: (<svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="2" /><path d="M16.24 7.76a6 6 0 0 1 0 8.49M7.76 16.24a6 6 0 0 1 0-8.49M19.07 4.93a10 10 0 0 1 0 14.14M4.93 19.07a10 10 0 0 1 0-14.14" /></svg>),
    },
    {
        color: '#10B981',
        t: 'Verified recruiter contacts',
        d: 'Get real recruiter emails and LinkedIn profiles for every company you target.',
        icon: (<svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" /><path d="M22 7l-10 6L2 7" /></svg>),
    },
    {
        color: '#E11D48',
        t: 'AI resume tailoring',
        d: 'Tailor your resume to each role and check your ATS score before you apply.',
        icon: (<svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M16 13H8M16 17H8M10 9H8" /></svg>),
    },
    {
        color: '#0EA5E9',
        t: 'Guided daily plan',
        d: 'A focused daily action list keeps your search on track and momentum high.',
        icon: (<svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="M22 4L12 14.01l-3-3" /></svg>),
    },
];
const ALT_GRADIENT_TITLES = ['Hiring signals from peers', 'AI resume tailoring', 'Guided daily plan'];

const ConnectLinkedInHero = () => {
    const dispatch = useDispatch();
    const { isLoading: liConnectLoading } = useSelector((state) => state.liauth);

    const handleLIConnect = () => dispatch(linkedInLogin());

    return (
        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-5" style={{ background: 'linear-gradient(160deg, #F8FAFF 0%, #EEF1FE 45%, #F5F3FF 100%)' }}>
            {/* Hero connect card */}
            <div className="rounded-2xl overflow-hidden border border-[#C7D0F8]">
                <div className="grid grid-cols-1 md:grid-cols-2 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1F2A56 0%, #3F57E0 60%, #6478F0 100%)', color: '#fff' }}>
                    {/* Left */}
                    <div className="p-8 relative z-10 text-left">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/25 text-[11px] font-semibold tracking-wide uppercase mb-4">
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" /><circle cx="4" cy="4" r="2" /></svg>
                            Connect LinkedIn to start
                        </div>
                        <h2 className="text-[26px] font-extrabold text-white leading-tight tracking-tight">
                            Unlock your<br />second-degree network
                        </h2>
                        <p className="text-[13.5px] text-white/85 mt-3 leading-relaxed max-w-md">
                            Champ analyzes your 1st and 2nd-degree connections to surface the people most likely to refer you — recruiters, hiring managers, and peers at your target companies.
                        </p>
                        <div className="mt-6">
                            <button
                                onClick={handleLIConnect}
                                disabled={liConnectLoading}
                                className="inline-flex items-center gap-2.5 px-5 py-3 bg-white text-[#0A66C2] rounded-xl text-[14px] font-bold hover:bg-gray-50 transition-colors disabled:opacity-60"
                                style={{ boxShadow: '0 8px 22px rgba(10,102,194,.25)' }}
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" /><circle cx="4" cy="4" r="2" /></svg>
                                {liConnectLoading ? 'Connecting…' : 'Connect LinkedIn'}
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                            </button>
                            <p className="text-[11.5px] text-white/70 mt-2.5">Takes 30 seconds · read-only · revoke anytime</p>
                        </div>
                        <div className="flex gap-5 mt-5 flex-wrap">
                            {[
                                { l: '32,400+', s: 'job seekers connected' },
                                { l: '4.2×', s: 'more responses with referrals' },
                                { l: 'Read-only', s: 'never posts on your behalf' },
                            ].map((s, i) => (
                                <div key={i} className={`flex items-center gap-2 ${i > 0 ? 'pl-5 border-l border-white/20' : ''}`}>
                                    <svg className="w-3 h-3 text-emerald-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                    <div>
                                        <div className="text-[13px] font-bold text-white leading-none">{s.l}</div>
                                        <div className="text-[10.5px] text-white/70 mt-0.5">{s.s}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {/* Right — network SVG */}
                    <div className="hidden md:flex items-center justify-center p-8">
                        <svg viewBox="0 0 360 280" width="100%" style={{ maxWidth: 380 }}>
                            <defs>
                                <radialGradient id="ci-glow" cx="50%" cy="50%" r="50%">
                                    <stop offset="0%" stopColor="rgba(255,255,255,0.4)" />
                                    <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                                </radialGradient>
                            </defs>
                            <circle cx="180" cy="140" r="120" fill="url(#ci-glow)" opacity=".5" />
                            {[[180, 140, 60, 60], [180, 140, 300, 70], [180, 140, 80, 200], [180, 140, 290, 220], [180, 140, 200, 40], [180, 140, 110, 140], [180, 140, 260, 150], [180, 140, 170, 250]].map(([x1, y1, x2, y2], i) => (
                                <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(255,255,255,.32)" strokeWidth="1" strokeDasharray="3 4" />
                            ))}
                            <circle cx="180" cy="140" r="32" fill="#fff" />
                            <text x="180" y="146" textAnchor="middle" fontSize="14" fontWeight="800" fill="#1F2A56" fontFamily="Inter,sans-serif">YOU</text>
                            {[{ x: 60, y: 60, i: "MC", c: "#FBBF24" }, { x: 300, y: 70, i: "DH", c: "#F472B6" }, { x: 80, y: 200, i: "KP", c: "#60A5FA" }, { x: 290, y: 220, i: "JR", c: "#34D399" }].map((p, i) => (
                                <g key={i}><circle cx={p.x} cy={p.y} r="20" fill={p.c} /><text x={p.x} y={p.y + 4} textAnchor="middle" fontSize="10" fontWeight="700" fill="#1F2A56" fontFamily="Inter,sans-serif">{p.i}</text></g>
                            ))}
                            {[{ x: 200, y: 40 }, { x: 110, y: 140 }, { x: 260, y: 150 }, { x: 170, y: 250 }].map((p, i) => (
                                <circle key={i} cx={p.x} cy={p.y} r="11" fill="rgba(255,255,255,.7)" stroke="rgba(255,255,255,.4)" strokeWidth="1" />
                            ))}
                        </svg>
                    </div>
                </div>
            </div>

            {/* Feature tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {FEATURES.map((f, i) => (
                    <div
                        key={i}
                        className="group rounded-2xl border p-5 flex flex-col items-center text-center transition-all hover:-translate-y-0.5 hover:shadow-md"
                        style={{
                            boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
                            background: ALT_GRADIENT_TITLES.includes(f.t)
                                ? `radial-gradient(125% 95% at 100% 0%, ${f.color}2e 0%, ${f.color}0a 48%, #ffffff 100%)`
                                : `linear-gradient(150deg, ${f.color}24 0%, ${f.color}0d 38%, #ffffff 100%)`,
                            borderColor: `${f.color}2e`,
                        }}
                    >
                        <div
                            className="w-11 h-11 rounded-xl flex items-center justify-center mb-3.5 transition-transform group-hover:scale-105"
                            style={{ background: `linear-gradient(135deg, ${f.color}22, ${f.color}0d)`, color: f.color, border: `1px solid ${f.color}26` }}
                        >
                            {f.icon}
                        </div>
                        <p className="text-[14px] font-extrabold text-[#0F172A] tracking-tight text-center">{f.t}</p>
                        <p className="text-[12.5px] text-[#64748B] mt-1.5 leading-relaxed text-center">{f.d}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ConnectLinkedInHero;
